import { AlertTriangle, Building2, MapPin, ShieldAlert, Users } from "lucide-react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import type { DistrictRow } from "@busright/data-contract";

import { ChartCard } from "@/components/ChartCard";
import { DistWorklistTable } from "@/components/DistWorklistTable";
import { EmptyState } from "@/components/EmptyState";
import { InsightCallout, type InsightStat } from "@/components/InsightCallout";
import { KpiCard } from "@/components/KpiCard";
import { NotesList } from "@/components/NotesList";
import { PageHeader } from "@/components/PageHeader";
import { useDistricts } from "@/hooks/queries";
import { formatInt, formatPct } from "@/lib/format";

// Districts — the hardware ↔ recipient bridge. A real map ships later; for now this is the
// match-coverage callout (217 matched / 278 unmatched DQ / 495 total) plus a ranked district
// worklist and a sortable table. RAG color is allowed here because the ranking is driven by
// device health/risk (risk_tier), which is exactly what RAG is reserved for.

const tierLevel = (r: DistrictRow): number => {
  if (!r.riskCounts) {
    return 0;
  }
  return r.riskCounts.critical * 1000 + r.riskCounts.warn;
};

export const DistrictsPage = (): ReactNode => {
  const { data, isLoading, isError, error } = useDistricts();
  const navigate = useNavigate();
  const meta = data?.meta;
  const payload = data?.data;

  const rows = payload?.rows ?? [];
  const total = payload ? payload.matchedCount + payload.unmatchedCount : 0;
  const matchRate = total > 0 && payload ? (payload.matchedCount / total) * 100 : 0;

  // Matched, device-bearing districts ranked by at-risk load (CRIT first, then WARN).
  const ranked = rows
    .filter((r) => r.districtId !== null && (r.deviceCount ?? 0) > 0)
    .slice()
    .sort((a, b) => tierLevel(b) - tierLevel(a));

  const atRisk = ranked.filter((r) => (r.riskCounts?.critical ?? 0) > 0);

  // The "so what": how many districts carry at least one CRITICAL device, and the size of the
  // unmatched recipient DQ gap. Match coverage is a data-quality story (neutral), not a health/RAG
  // story — so the callout stays "info", never red/amber.
  const insightStats: readonly InsightStat[] = payload
    ? [
        { label: "districts ≥1 CRITICAL", value: formatInt(atRisk.length) },
        { label: "unmatched recipients (DQ)", value: formatInt(payload.unmatchedCount) },
        { label: "match rate", value: formatPct(matchRate) },
      ]
    : [];
  const insightHeadline = payload
    ? `${formatInt(atRisk.length)} ${atRisk.length === 1 ? "district has" : "districts have"} ≥1 CRITICAL device; ${formatInt(payload.unmatchedCount)} of ${formatInt(total)} recipients are still unmatched (a data-quality gap, not a health risk).`
    : "";

  if (isError) {
    return (
      <>
        <PageHeader title="Districts" meta={meta} />
        <ChartCard title="Districts">
          <EmptyState
            title="Couldn't load districts"
            message={error instanceof Error ? error.message : "Unknown error"}
            icon={AlertTriangle}
          />
        </ChartCard>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Districts"
        description="The hardware ↔ recipient bridge — match coverage, an at-risk worklist, and a ranked district table. A live map ships later."
        meta={meta}
      />

      {payload ? (
        <InsightCallout
          headline={insightHeadline}
          severity="info"
          stats={insightStats}
          cta={{ label: "Open Fleet Health", to: "/health" }}
        />
      ) : null}

      <div className="grid kpi-band">
        <KpiCard
          label="Recipients matched"
          icon={Building2}
          loading={isLoading}
          value={payload ? formatInt(payload.matchedCount) : "—"}
          source="account_map"
        />
        <KpiCard
          label="Unmatched (DQ)"
          icon={ShieldAlert}
          loading={isLoading}
          value={payload ? formatInt(payload.unmatchedCount) : "—"}
          source="account_map.unmatched"
        />
        <KpiCard
          label="Recipients total"
          icon={Users}
          loading={isLoading}
          value={total > 0 ? formatInt(total) : "—"}
          source="stg_e2_orders.location_name"
        />
        <KpiCard
          label="Match rate"
          icon={MapPin}
          loading={isLoading}
          value={total > 0 ? formatPct(matchRate) : "—"}
          source="account_map"
        />
      </div>

      <div style={{ marginBottom: 16 }}>
        <ChartCard
          title="Match coverage"
          subtitle="Recipient → district_id match — the unmatched count is a DATA-QUALITY backlog (recipients we can't yet attribute to a district), NOT a fleet-health risk"
          source="account_map"
          loading={isLoading}
        >
          {payload ? (
            <div className="metric-strip">
              <div className="metric-chip">
                <span className="mc-label">Matched to a district</span>
                <span className="mc-value br-tabular-nums">{formatInt(payload.matchedCount)}</span>
              </div>
              <div className="metric-chip">
                <span className="mc-label">Unmatched (DQ backlog)</span>
                <span className="mc-value br-tabular-nums">
                  {formatInt(payload.unmatchedCount)}
                </span>
              </div>
              <div className="metric-chip">
                <span className="mc-label">Total recipients</span>
                <span className="mc-value br-tabular-nums">{formatInt(total)}</span>
              </div>
              <div className="metric-chip">
                <span className="mc-label">Match rate</span>
                <span className="mc-value br-tabular-nums">{formatPct(matchRate)}</span>
              </div>
            </div>
          ) : (
            <EmptyState title="No coverage data" />
          )}
        </ChartCard>
      </div>

      <div style={{ marginBottom: 16 }}>
        <ChartCard
          title="At-risk districts"
          subtitle="Matched districts with ≥1 CRITICAL device — click a row to expand its at-risk composition and drill into the device tail"
          source="rpt_hw_district_latest"
          loading={isLoading}
        >
          <DistWorklistTable
            rows={atRisk}
            onOpenDeviceTail={() => {
              void navigate("/health");
            }}
            emptyTitle="No districts with critical devices"
          />
        </ChartCard>
      </div>

      <ChartCard
        title="District worklist"
        subtitle="Matched, device-bearing districts ranked by at-risk load (CRIT, then WARN) — click a row to expand its devices"
        source="rpt_hw_district_latest"
        loading={isLoading}
      >
        <DistWorklistTable
          rows={ranked}
          showRank
          onOpenDeviceTail={() => {
            void navigate("/health");
          }}
          emptyTitle="No districts"
        />
      </ChartCard>

      {meta ? (
        <div style={{ marginTop: 16 }}>
          <ChartCard title="Data notes" loading={isLoading}>
            <NotesList notes={meta.notes} />
          </ChartCard>
        </div>
      ) : null}
    </>
  );
};
